// src/components/ia-paca/MetierPageTemplate.tsx
//
// Template factorisé pour les pages métier IA PACA — utilisé par les
// métiers "secondaires" (Prompt Engineer, AI PM, Architecte IA, etc.).
// Les pages "premium" (AI Engineer, ML Engineer, MLOps, Data Scientist)
// gardent leur structure custom plus riche.

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../layout/layout';
import IaPacaHero from './IaPacaHero';
import IaPacaCTA from './IaPacaCTA';
import IaSalaryTable from './IaSalaryTable';
import { ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import type { ComponentType } from 'react';
import { sanitizeHtml } from '../../lib/sanitizeHtml';
import { safeJsonLd } from '../../lib/utils/schema-utils';

// =========================================================================
// Types
// =========================================================================

interface VilleSalaire {
  ville: string;
  junior?: string;
  confirme: string;
  senior: string;
  expert?: string;
}

interface QualifQuestion {
  q: string;
  good: string;
  bad: string;
}

interface StackCategory {
  Icon: ComponentType<{ className?: string }>;
  title: string;
  tools: string[];
}

export interface MetierPageConfig {
  // SEO
  slug: string; // ex. 'prompt-engineer'
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  // Hero
  heroLabel: string; // ex. "Prompt Engineer"
  heroSubtitle: string;
  heroBadges: string[];
  // JSON-LD Occupation
  jsonLdName: string;
  jsonLdDescription: string;
  jsonLdSkills: string;
  jsonLdMinSalary: number;
  jsonLdMedianSalary: number;
  jsonLdMaxSalary: number;
  jsonLdEducation: string;
  jsonLdExperience: string;
  // Le métier en clair
  intro: { title: string; paragraphs: string[] };
  // Stack technique
  stackTitle: string;
  stack: StackCategory[];
  // Salaires par ville
  salaryByCityTitle: string;
  salaryByCity: VilleSalaire[];
  // Questions de qualification
  questionsTitle: string;
  questionsIntro: string;
  questions: QualifQuestion[];
  // CTA
  ctaTitle: string;
  ctaSubtitle: string;
}

// =========================================================================
// Composant
// =========================================================================

export default function MetierPageTemplate({ config }: { config: MetierPageConfig }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    name: config.jsonLdName,
    description: config.jsonLdDescription,
    occupationLocation: [
      { '@type': 'City', name: 'Marseille' },
      { '@type': 'City', name: 'Sophia Antipolis' },
      { '@type': 'City', name: 'Aix-en-Provence' },
      { '@type': 'City', name: 'Nice' },
      { '@type': 'AdministrativeArea', name: "Provence-Alpes-Côte d'Azur" },
    ],
    estimatedSalary: [
      {
        '@type': 'MonetaryAmountDistribution',
        currency: 'EUR',
        duration: 'P1Y',
        percentile10: config.jsonLdMinSalary,
        median: config.jsonLdMedianSalary,
        percentile90: config.jsonLdMaxSalary,
      },
    ],
    skills: config.jsonLdSkills,
    educationRequirements: config.jsonLdEducation,
    experienceRequirements: config.jsonLdExperience,
  };

  return (
    <Layout
      title={config.metaTitle}
      description={config.metaDescription}
      keywords={config.metaKeywords}
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: config.heroLabel, href: `/ia-paca/metiers/${config.slug}` },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{safeJsonLd(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Métier IA · PACA"
        title="Recruter un"
        highlight={config.heroLabel}
        subtitle={config.heroSubtitle}
        badges={config.heroBadges}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Tous les métiers IA
          </Link>
        </div>
      </div>

      {/* Intro */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Le métier — sans bullshit
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {config.intro.title}
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            {config.intro.paragraphs.map((p, i) => (
              <p
                key={i}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(p) }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stack */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Stack technique 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {config.stackTitle}
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            {config.stack.map((s) => (
              <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2">
                  <s.Icon className="h-5 w-5 text-violet-600" />
                  <h3 className="text-base font-bold text-slate-900">{s.title}</h3>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.tools.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Salaires par ville */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires en PACA — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {config.salaryByCityTitle}
          </h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-4 py-3">Ville</th>
                    <th className="px-4 py-3">Junior</th>
                    <th className="px-4 py-3">Confirmé</th>
                    <th className="px-4 py-3">Senior</th>
                    <th className="px-4 py-3">Expert / Lead</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {config.salaryByCity.map((r) => (
                    <tr key={r.ville} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-semibold text-slate-900">{r.ville}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-700">{r.junior ?? '—'}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-700">{r.confirme}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-700">{r.senior}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-700">{r.expert ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Questions de qualification */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Détecter les bons profils
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {config.questionsTitle}
          </h2>
          <p className="mt-3 text-base text-slate-600">{config.questionsIntro}</p>
          <div className="mt-8 space-y-4">
            {config.questions.map((q, i) => (
              <div
                key={q.q}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900">{q.q}</h3>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Bon signe
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-emerald-900">
                          {q.good}
                        </p>
                      </div>
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-700">
                          <XCircle className="h-3.5 w-3.5" />
                          Drapeau rouge
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-rose-900">{q.bad}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tableau global PACA */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Comparer avec les autres métiers IA en PACA
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Tableau global des salaires IA en PACA.
          </h2>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      <IaPacaCTA title={config.ctaTitle} subtitle={config.ctaSubtitle} />
    </Layout>
  );
}
